<template>
    <v-container class="artigos-home pt-6 pb-12">
        <div class="home-header">
            <div class="home-header__intro">
                <eyebrow primary text="Artigos para o seu site" />
                <h1 class="home-header__title">O que você quer fazer hoje?</h1>
                <p class="home-header__subtitle ma-0">
                    Escolha um caminho abaixo. Se ficar com dúvida, toque no botão de ajuda
                    <v-icon size="14" color="primary">mdi-help-circle-outline</v-icon>
                    a qualquer momento.
                </p>
            </div>
            <v-tooltip v-if="credits !== null" bottom>
                <template #activator="{ on, attrs }">
                    <div class="home-credits" v-on="on" v-bind="attrs">
                        <v-icon size="18" :color="credits === 0 ? 'warning' : 'primary'">mdi-flash</v-icon>
                        <span class="home-credits__value">{{ credits }}</span>
                        <span class="home-credits__label">{{ credits === 1 ? 'crédito' : 'créditos' }}</span>
                    </div>
                </template>
                <span>Cada artigo criado usa 1 crédito do seu plano.</span>
            </v-tooltip>
        </div>

        <div v-if="loading && !overview" class="mb-8">
            <v-skeleton-loader type="article" />
        </div>

        <glass-panel v-else-if="pendingNotices.length || awaitingReview.length" padding="6" class="mb-8 pending-panel">
            <section-header title="Artigos que precisam da sua atenção" icon="mdi-bell-badge-outline" />

            <info-notice
                v-if="awaitingReview.length"
                variant="warning"
                icon="mdi-email-open-outline"
                class="mb-3"
            >
                <template #title>
                    {{ awaitingReview.length === 1
                        ? 'Um artigo está pronto e esperando a sua leitura para ir ao ar.'
                        : `${awaitingReview.length} artigos estão prontos e esperando a sua leitura para irem ao ar.` }}
                </template>
                <div class="pending-articles">
                    <button
                        v-for="article in awaitingReviewTop"
                        :key="article._id"
                        type="button"
                        class="pending-article"
                        @click="goToReview(article)"
                    >
                        <v-icon size="16" color="primary" class="mr-1">mdi-file-document-outline</v-icon>
                        <span class="pending-article__title">{{ article.title || 'Artigo sem título' }}</span>
                        <span class="pending-article__cta">Revisar agora
                            <v-icon size="13" color="primary">mdi-arrow-right</v-icon>
                        </span>
                    </button>
                    <span v-if="awaitingReview.length > 3" class="pending-article__more">
                        e mais {{ awaitingReview.length - 3 }}…
                    </span>
                </div>
            </info-notice>

            <info-notice
                v-for="notice in pendingNotices"
                :key="notice.key"
                :variant="notice.variant"
                :icon="notice.icon"
                :title="notice.title"
                :description="notice.description"
                class="mb-3"
            >
                <template #actions>
                    <v-btn small outlined color="primary" class="notice-btn" @click="notice.action()">
                        {{ notice.actionLabel }}
                    </v-btn>
                </template>
            </info-notice>
        </glass-panel>

        <ArtigosChecklist
            v-if="overview"
            :overview="overview"
            class="mb-8"
            @go="$emit('go', $event)"
        />

        <v-row class="paths-row" dense>
            <v-col cols="12" md="6">
                <hero-card
                    class="path-card"
                    icon="mdi-creation"
                    title="Criar 1 artigo agora"
                    cta="Começar"
                    :height="pathCardHeight"
                    role="button"
                    tabindex="0"
                    @click="$emit('go', 'criar')"
                    @keyup.enter="$emit('go', 'criar')"
                >
                    Escolha um tema, e a nossa inteligência artificial escreve um artigo completo
                    para o seu site. Você lê, ajusta se quiser e publica. Leva poucos minutos.
                    <span v-if="credits === 0" class="path-card__warn">
                        <v-icon size="14" color="warning">mdi-alert-outline</v-icon>
                        Seus créditos acabaram — será preciso aguardar a renovação do plano.
                    </span>
                </hero-card>
            </v-col>
            <v-col cols="12" md="6">
                <hero-card
                    class="path-card"
                    icon="mdi-calendar-sync-outline"
                    title="Deixar no piloto automático"
                    cta="Ativar publicação automática"
                    :height="pathCardHeight"
                    role="button"
                    tabindex="0"
                    @click="$emit('go', 'calendario')"
                    @keyup.enter="$emit('go', 'calendario')"
                >
                    Você escolhe os dias da semana. Nós escrevemos os artigos e avisamos por
                    e-mail para você aprovar. Aprovou, publicamos no seu site sozinhos.
                </hero-card>
            </v-col>
        </v-row>

        <div class="home-shortcuts">
            <glass-panel tag="button" type="button" variant="soft" class="shortcut" @click="$emit('go', 'artigos')">
                <v-icon size="20" color="primary">mdi-view-list</v-icon>
                <span class="shortcut__label">Meus artigos</span>
                <span v-if="counts.articles" class="shortcut__count">{{ counts.articles }}</span>
                <v-icon size="16" class="shortcut__arrow">mdi-chevron-right</v-icon>
            </glass-panel>
            <glass-panel tag="button" type="button" variant="soft" class="shortcut" @click="$emit('go', 'calendario')">
                <v-icon size="20" color="primary">mdi-calendar-month</v-icon>
                <span class="shortcut__label">Piloto automático</span>
                <span v-if="counts.schedules" class="shortcut__count">{{ counts.schedules }}</span>
                <v-icon size="16" class="shortcut__arrow">mdi-chevron-right</v-icon>
            </glass-panel>
            <glass-panel tag="button" type="button" variant="soft" class="shortcut" @click="$emit('open-guide')">
                <v-icon size="20" color="primary">mdi-help-circle-outline</v-icon>
                <span class="shortcut__label">Como tudo isso funciona?</span>
                <v-icon size="16" class="shortcut__arrow">mdi-chevron-right</v-icon>
            </glass-panel>
        </div>
    </v-container>
</template>

<script>
import ArtigosChecklist from '@/components/ArtigosChecklist.vue'

export default {
    name: 'ArtigosHome',
    components: { ArtigosChecklist },
    props: {
        overview: { type: Object, default: null },
        loading: { type: Boolean, default: false },
    },
    computed: {
        pending() {
            return this.overview?.pending || {}
        },
        counts() {
            return this.overview?.counts || {}
        },
        credits() {
            const c = this.pending.credits
            return typeof c === 'number' ? c : null // null = admin (sem cota)
        },
        awaitingReview() {
            return Array.isArray(this.pending.awaiting_review) ? this.pending.awaiting_review : []
        },
        awaitingReviewTop() {
            return this.awaitingReview.slice(0, 3)
        },
        failedSchedules() {
            return Array.isArray(this.pending.failed_schedules) ? this.pending.failed_schedules : []
        },
        pathCardHeight() {
            return this.$vuetify.breakpoint.smAndDown ? 260 : 300
        },
        pendingNotices() {
            const notices = []

            if (this.credits === 0) {
                notices.push({
                    key: 'no-credits',
                    variant: 'error',
                    icon: 'mdi-flash-off-outline',
                    title: 'Seus créditos acabaram.',
                    description: 'Sem créditos, não conseguimos criar novos artigos — os agendados ficam em espera e voltam sozinhos quando o saldo voltar.',
                    actionLabel: 'Entender créditos',
                    action: () => this.$emit('open-guide', 'credits'),
                })
            }

            const failedNoCredits = this.failedSchedules.filter(s => s.generation_error === 'no_credits')
            if (failedNoCredits.length) {
                notices.push({
                    key: 'failed-no-credits',
                    variant: 'error',
                    icon: 'mdi-calendar-alert-outline',
                    title: failedNoCredits.length === 1
                        ? 'Um artigo agendado não pôde ser criado por falta de créditos.'
                        : `${failedNoCredits.length} artigos agendados não puderam ser criados por falta de créditos.`,
                    description: 'Quando o saldo voltar, eles serão criados sozinhos.',
                    actionLabel: 'Ver no calendário',
                    action: () => this.$emit('go', 'calendario'),
                })
            }

            const failedPublish = this.failedSchedules.filter(s => s.generation_error === 'publish_failed')
            if (failedPublish.length) {
                notices.push({
                    key: 'failed-publish',
                    variant: 'error',
                    icon: 'mdi-cloud-alert-outline',
                    title: failedPublish.length === 1
                        ? 'Um artigo foi criado, mas não conseguimos publicá-lo no seu site.'
                        : `${failedPublish.length} artigos foram criados, mas não conseguimos publicá-los no seu site.`,
                    description: 'O conteúdo está salvo em "Meus artigos". Veja os detalhes no calendário.',
                    actionLabel: 'Ver no calendário',
                    action: () => this.$emit('go', 'calendario'),
                })
            }

            const unclassified = this.pending.unclassified_themes || 0
            if (unclassified > 0) {
                notices.push({
                    key: 'themes',
                    variant: 'primary',
                    icon: 'mdi-lightbulb-on-outline',
                    title: unclassified === 1
                        ? 'Você tem 1 ideia de tema esperando a sua decisão.'
                        : `Você tem ${unclassified} ideias de temas esperando a sua decisão.`,
                    description: 'Diga quais você quer usar — elas viram artigos para o seu site.',
                    actionLabel: 'Escolher temas',
                    action: () => this.$emit('go', 'criar'),
                })
            }

            if (this.pending.profile_incomplete) {
                notices.push({
                    key: 'profile',
                    variant: 'warning',
                    icon: 'mdi-account-alert-outline',
                    title: 'Complete o seu perfil.',
                    description: 'Com o perfil preenchido, conseguimos sugerir temas sob medida para a sua especialidade.',
                    actionLabel: 'Completar perfil',
                    action: () => this.$router.push('/meu-perfil'),
                })
            }

            if (this.pending.settings_incomplete && (this.counts.schedules || 0) > 0) {
                notices.push({
                    key: 'settings',
                    variant: 'warning',
                    icon: 'mdi-cog-outline',
                    title: 'Defina o autor e a imagem padrão.',
                    description: 'Eles são usados nas publicações automáticas do calendário.',
                    actionLabel: 'Definir agora',
                    action: () => this.$emit('go', 'calendario'),
                })
            }

            return notices
        },
    },
    methods: {
        goToReview(article) {
            if (!article?._id) return
            this.$router.push(`/articles/review/${article._id}`)
        },
    },
}
</script>

<style scoped>
.home-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 28px;
}

.home-header__intro {
    flex: 1 1 auto;
    min-width: 0;
}

.home-header__title {
    font-family: var(--font-display, inherit);
    font-size: 28px;
    font-weight: 800;
    line-height: 1.2;
    color: var(--color-on-surface, #2d2e37);
    margin: 6px 0 6px;
}

.home-header__subtitle {
    font-size: 14px;
    line-height: 1.55;
    color: var(--color-on-surface-variant, #5b5b64);
    max-width: 560px;
}

.home-credits {
    flex-shrink: 0;
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 8px 14px;
    border-radius: var(--radius-pill, 9999px);
    border: 1px solid var(--glass-panel-border, rgba(112, 105, 161, 0.22));
    background: var(--glass-soft-bg, rgba(255, 255, 255, 0.4));
    cursor: default;
}

.home-credits .v-icon {
    align-self: center;
}

.home-credits__value {
    font-size: 18px;
    font-weight: 800;
    color: var(--color-on-surface, #2d2e37);
    font-variant-numeric: tabular-nums;
}

.home-credits__label {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant, #5b5b64);
}

.pending-articles {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
}

.pending-article {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: var(--radius-sm, 12px);
    border: 1px solid var(--glass-panel-border, rgba(112, 105, 161, 0.22));
    background: var(--glass-soft-bg, rgba(255, 255, 255, 0.4));
    cursor: pointer;
    text-align: left;
    transition: border-color 160ms ease, background 160ms ease;
}

.pending-article:hover {
    border-color: var(--color-primary, #702ae1);
    background: rgba(112, 42, 225, 0.06);
}

.pending-article__title {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-on-surface, #2d2e37);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pending-article__cta {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    font-weight: 700;
    color: var(--color-primary, #702ae1);
    white-space: nowrap;
}

.pending-article__more {
    font-size: 12px;
    color: var(--color-on-surface-variant, #5b5b64);
    padding-left: 4px;
}

.notice-btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 700;
}

.paths-row {
    margin-bottom: 24px;
}

.path-card {
    cursor: pointer;
}

.path-card:focus-visible {
    outline: 2px solid rgba(112, 42, 225, 0.45);
    outline-offset: 3px;
}

.path-card__warn {
    display: block;
    margin-top: 10px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--color-on-surface-variant, #5b5b64);
}

.home-shortcuts {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.shortcut {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 18px;
    border-radius: var(--radius-pill, 9999px);
    cursor: pointer;
    font-family: inherit;
    transition: border-color 160ms ease, transform 160ms ease;
}

.shortcut:hover {
    border-color: var(--color-primary, #702ae1);
    transform: translateY(-2px);
}

.shortcut__label {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--color-on-surface, #2d2e37);
}

.shortcut__count {
    min-width: 22px;
    padding: 1px 7px;
    border-radius: 9999px;
    background: rgba(112, 42, 225, 0.12);
    color: var(--color-primary, #702ae1);
    font-size: 11.5px;
    font-weight: 800;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.shortcut__arrow {
    color: var(--color-on-surface-variant, #5b5b64) !important;
}

@media (max-width: 700px) {
    .home-header {
        flex-direction: column;
    }

    .home-header__title {
        font-size: 23px;
    }

    .home-shortcuts {
        flex-direction: column;
        align-items: stretch;
    }

    .shortcut {
        justify-content: flex-start;
    }

    .shortcut__arrow {
        margin-left: auto;
    }
}
</style>
